import React from 'react';
import { Badge } from 'react-bootstrap';
import { DATE_FORMAT_WEB } from 'Constants/DateFormatConstants';
import { DateTimeFormater } from 'Helpers/DateTimeHelper';

/** Standard filter params shared by all text/date columns */
const standardFilterParams = { apply: true, newRowsAction: 'keep' };

/**
 * Creates a standard text column definition.
 * @param {string} headerName - Display name in the header
 * @param {string} field - Data field name
 * @param {Object} [overrides] - Additional properties to merge
 * @returns {Object} Column definition
 */
export const textColumn = (headerName, field, overrides = {}) => ({
  headerName,
  field,
  filter: 'agTextColumnFilter',
  filterParams: standardFilterParams,
  suppressFloatingFilterButton: true,
  sortable: true,
  width: 150,
  ...overrides,
});

/**
 * Creates a standard date column definition with DateTimeFormater.
 * @param {string} headerName - Display name in the header
 * @param {string} field - Data field name
 * @param {Object} [overrides] - Additional properties to merge
 * @returns {Object} Column definition
 */
export const dateColumn = (headerName, field, overrides = {}) => ({
  headerName,
  field,
  filter: 'agDateColumnFilter',
  filterParams: standardFilterParams,
  suppressFloatingFilterButton: true,
  sortable: true,
  width: 150,
  valueFormatter: (params) => {
    if (params.value) {
      return DateTimeFormater(params.value, null, DATE_FORMAT_WEB);
    }
    return '';
  },
  ...overrides,
});

/** Reusable column definitions shared across multiple AgGrid tables */
export const firstName = (overrides = {}) => textColumn('First Name', 'firstName', overrides);
export const lastName = (overrides = {}) => textColumn('Last Name', 'lastName', overrides);
export const destination = () => textColumn('Destination', 'destination');
export const country = () => textColumn('Country', 'country');
export const iataCode = () => textColumn('IATA Code', 'iataCode', { width: 110 });
export const season = () => textColumn('Season', 'season');
export const staffSourceMarket = () => textColumn('Source Market (staff)', 'staffSourceMarket');
export const typeOfFlight = () => ({
  headerName: 'Type of flight',
  field: 'typeOfFlight',
  filter: 'agTextColumnFilter',
  filterParams: { apply: true, newRowsAction: 'keep' },
  suppressFloatingFilterButton: true,
  width: 150,
});
export const preferredFlightDate = () => dateColumn('Prefered flight date', 'preferredFlightDate');
export const plannedAssignmentStartDate = () => dateColumn('Planned assignment date', 'plannedAssignmentStartDate');
export const greenLightUpdated = (headerName = 'Greenlighted date HR') => dateColumn(headerName, 'greenLightUpdated');

/**
 * Confirmed flight date column with complex formatter that reads from flights array.
 * @returns {Object} Column definition
 */
export const confirmedFlightDate = () =>
  dateColumn('Confirmed flight date', 'confirmedFlightDate', {
    valueFormatter: (params) => {
      if (params.data && params.data.flights && Array.isArray(params.data.flights) && params.data.flights.length > 0) {
        const lastFlight = params.data.flights[params.data.flights.length - 1];
        if (lastFlight && lastFlight.confirmedFlightDate) {
          return DateTimeFormater(lastFlight.confirmedFlightDate, null, DATE_FORMAT_WEB);
        }
      }
      return '';
    },
  });

/**
 * Assignment type column that renders an EC or MPLID badge.
 * Tickets without assignmentType (historical) default to MPLID.
 * @returns {Object} Column definition
 */
export const assignmentType = () => ({
  headerName: 'Type',
  field: 'assignmentType',
  filter: 'agTextColumnFilter',
  filterParams: standardFilterParams,
  suppressFloatingFilterButton: true,
  sortable: true,
  width: 90,
  cellRenderer: (params) => {
    if (params.value === 'ec') {
      return <Badge bg="info">EC</Badge>;
    }
    return <Badge bg="secondary">MPLID</Badge>;
  },
});
